import React from 'react';
import styled, { css } from 'styled-components';
import {
    Column,
    HBTableColumnHeaderProps,
    DraggingContainerProps
} from './HBTable.types';
import HBText from '../HBText/HBText';
import HBIconButton from '../HBIconButton/HBIconButton';
import { vars } from '../../theme';
import { useSyncElementDimension } from '../../hooks/useSyncElementDimension';
import HBTableCheckbox from './HBTableCheckbox';
import HBClickable from '../HBClickable/HBClickable';
import { ArrowLineDown12, ArrowLineUp12 } from '@honeybook/icons';
import HBInfoIcon from '../HBInfoIcon/HBInfoIcon';
import { directHoverSelector } from '../../styles/mixins';
import { useSortable } from '@dnd-kit/sortable';
import {
    draggingContainerStyles,
    DraggingShadow,
    getColumnTransform
} from './HBTable.styles';

interface CheckboxProps<T> {
    table: HBTableColumnHeaderProps<T>['table'];
    column: HBTableColumnHeaderProps<T>['column'];
}

export const useIsColumnPinned = <T,>(
    table: HBTableColumnHeaderProps<T>['table'],
    column: HBTableColumnHeaderProps<T>['column']
) => {
    const { isFirstColumnPinned, isLastColumnPinned } = table.options.meta;

    return (
        (isFirstColumnPinned && column.getIndex() === 0) ||
        (isLastColumnPinned &&
            column.getIndex() === table.getAllColumns().length - 1)
    );
};

export const useSortableColumn = <T,>(
    table: HBTableColumnHeaderProps<T>['table'],
    column: HBTableColumnHeaderProps<T>['column']
) => {
    const columnIndex = column.getIndex();

    const isPinnedColumn = useIsColumnPinned(table, column);
    const dropIndex = isPinnedColumn
        ? !columnIndex
            ? 1
            : columnIndex - 1
        : columnIndex;

    return useSortable({
        id: column.id,
        disabled: isPinnedColumn,
        data: {
            overIndex: dropIndex
        }
    });
};

function HBTableColumnHeader<T>({
    column,
    tdEl,
    table
}: HBTableColumnHeaderProps<T>) {
    const { setTargetEl } = useSyncElementDimension<
        HTMLTableCellElement,
        HTMLTableCellElement
    >({ sourceEl: tdEl, dimension: 'width', strategy: 'max' });

    const { original } = column.columnDef.meta;
    const { testIdPrefix } = table.options.meta;

    const isPinnedColumn = useIsColumnPinned(table, column);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortableColumn(table, column);

    const handleSort =
        original.isSortable &&
        (() =>
            table.setSorting(({ 0: prevSort }) => [
                {
                    id: original.id,
                    desc:
                        prevSort.id === original.id
                            ? !prevSort.desc
                            : prevSort.desc
                }
            ]));

    const sorting = table.getState().sorting[0];
    const isSorted = sorting.id === original.id;
    const arrowIcon = sorting.desc ? ArrowLineDown12 : ArrowLineUp12;

    const className = [column.id];

    if (table.options.meta.isColumnDragAndDropEnabled) {
        className.push('draggable');
    }
    if (isPinnedColumn) {
        className.push('pinned');
    }

    if (isDragging) {
        className.push('dragging');
    }

    return (
        <TH
            {...attributes}
            {...listeners}
            className={className.join(' ')}
            $width={original.width}
            $isPinnedColumn={isPinnedColumn}
            $isDragging={isDragging}
            $isSortable={original.isSortable}
            $transition={transition}
            $isDnDEnabled={table.options.meta.isColumnDragAndDropEnabled}
            ref={
                original.width === 'fit'
                    ? ref => {
                          setTargetEl(ref);
                          setNodeRef(ref);
                      }
                    : setNodeRef
            }
            style={{
                transform: getColumnTransform(transform, isPinnedColumn) // Transform changes rapidly and causes styled components performance issues
            }}
            data-testid={`${testIdPrefix}-${original.title}-header`}
            aria-sort={
                !isSorted ? 'none' : sorting.desc ? 'descending' : 'ascending'
            }
            role="columnheader"
            tabIndex={-1}
        >
            <DraggingShadow $isDragging={isDragging}></DraggingShadow>

            <Container
                $isSorted={isSorted}
                $align={original.align}
                insetFocusState
            >
                <Checkbox table={table} column={column} />

                {original.prefix && (
                    <PrefixContainer>{original.prefix}</PrefixContainer>
                )}
                {original.title && (
                    <HBText
                        align={original.align ?? 'left'}
                        type="body12"
                        color="text.secondary"
                        transform="capitalize"
                    >
                        {original.title}
                    </HBText>
                )}
                {original.tooltip && <HBInfoIcon tooltip={original.tooltip} />}
                {original.isSortable && (
                    <HBIconButton
                        onClick={handleSort}
                        className={arrowClassNme}
                        size={'xxs'}
                        label={'sort-button'}
                        buttonTheme={'subtle-invisible'}
                        testId={`${testIdPrefix}-sort-icon-button`}
                        icon={arrowIcon}
                    ></HBIconButton>
                )}
            </Container>
        </TH>
    );
}

export default HBTableColumnHeader;

function Checkbox<T>({ table, column }: CheckboxProps<T>) {
    const { isInitialLoading, helper } = table.options.meta;

    if (
        column.getIndex() !== 0 ||
        isInitialLoading ||
        !helper.hasRowSelection
    ) {
        return null;
    }

    return (
        <HBTableCheckbox
            margin="0 16px 0 0"
            checked={helper.isAllCurrPageRowsSelected}
            indeterminate={helper.isSomeCurrPageRowsSelected}
            testIdName="header"
            onChange={e => {
                helper.toggleAllCurrPageRowsSelected(e.target.checked);
            }}
        />
    );
}
// -------------------------- Styles -------------------------- //
type THProps = {
    $width: Column<any>['width'];
    $isSortable: boolean;
    $isDnDEnabled: boolean;
} & DraggingContainerProps;
const TH = styled.th<THProps>`
    && {
        padding: 0;
        ${draggingContainerStyles}
        background-color: ${vars.colors.surface.itemInset};

        cursor: ${props => getThCursor(props)};
        ${({ $width }) =>
            $width === 'fit'
                ? css`
                      width: 0;
                  `
                : css`
                      max-width: ${$width}px;
                      min-width: ${$width}px;
                  `};
    }
`;

const arrowClassNme = 'sort-arrow';

const Container = styled(HBClickable)<{
    $isSorted: boolean;
    $align?: 'left' | 'right';
}>`
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: ${({ $align }) =>
        $align === 'right' ? 'flex-end' : 'flex-start'};
    gap: 8px;
    white-space: nowrap;

    .${arrowClassNme} {
        display: flex;
        opacity: ${({ $isSorted }) => ($isSorted ? 1 : 0)};
    }
    ${directHoverSelector},
    &:hover,
    &:focus-visible {
        .${arrowClassNme} {
            opacity: 1;
        }
    }
`;

const PrefixContainer = styled.div`
    th:last-child & {
        margin-left: auto;
    }
`;

function getThCursor(props: THProps): 'default' | 'grab' | 'grabbing' {
    if (!props.$isDnDEnabled || props.$isPinnedColumn) {
        return 'default';
    }

    if (props.$isDragging) {
        return 'grabbing';
    }

    return 'grab';
}
